import { Injectable, Injector } from '@angular/core';
import { Store } from '@ngrx/store';
import { Observable } from 'rxjs';

import { CategoriesService, InstallBaseService, ProductListService, ProductsService, SearchService } from '../services';
import { CatalogActions } from '../actions';
import * as fromState from '../reducers';
import * as CatalogSelectors from '../reducers/catalog.reducer';
import {
  IInstalledBaseSystems,
  IInstalledBaseSystemsSoldTo,
  ISimpleInstallBaseProduct,
} from '../models/installedbase.models';
import { ICatalogSearchIncluded, ICurrentParams } from '../models/catalog.models';
import { EGlueResource } from '../configurations/common';

@Injectable({
  providedIn: 'root',
})
export class CatalogFacade {
  private pCategoryService: CategoriesService;
  private pInstallBaseService: InstallBaseService;
  private pProductListService: ProductListService;
  private pProductsService: ProductsService;
  private pSearchService: SearchService;

  /**
   *
   * @returns {CategoriesService}
   */
  public get getCategoriesService(): CategoriesService {
    if (!this.pCategoryService) {
      this.pCategoryService = this.injector.get(CategoriesService);
    }
    return this.pCategoryService;
  }

  /**
   *
   * @returns {ProductListService}
   */
  public get productListService(): ProductListService {
    if (!this.pProductListService) {
      this.pProductListService = this.injector.get(ProductListService);
    }
    return this.pProductListService;
  }

  /**
   *
   * @returns {ProductsService}
   */
  public getProductsService(): ProductsService {
    if (!this.pProductsService) {
      this.pProductsService = this.injector.get(ProductsService);
    }
    return this.pProductsService;
  }

  /**
   *
   * @returns {InstallBaseService}
   */
  public getInstallBaseService(): InstallBaseService {
    if (!this.pInstallBaseService) {
      this.pInstallBaseService = this.injector.get(InstallBaseService);
    }
    return this.pInstallBaseService;
  }

  /**
   *
   * @returns {SearchService}
   */
  public getSearchService(): SearchService {
    if (!this.pSearchService) {
      this.pSearchService = this.injector.get(SearchService);
    }
    return this.pSearchService;
  }

  /**
   * @param {Injector} injector
   * @param {Store<State>} store
   */
  constructor(
    private injector: Injector,
    private store: Store<fromState.State>,
  ) {
  }

  /**
   *
   * @returns {Observable<any>}
   */
  getCategoryTree(): Observable<any> {
    return this.getCategoriesService.getCategoryTree();
  }

  /**
   *
   */
  loadCategories(): void {
    this.store.dispatch(CatalogActions.loadCategoriesStart());
  }

  /**
   *
   * @returns {Observable<any>}
   */
  selectCategories(): Observable<any> {
    return this.store.select(CatalogSelectors.selectCategories);
  }

  /**
   *
   * @returns {Observable<any>}
   */
  selectIsCategoriesLoaded(): Observable<any> {
    return this.store.select(CatalogSelectors.selectIsCategoriesLoaded);
  }

  /**
   *
   * @returns {Observable<any>}
   */
  selectIsCategoriesLoading(): Observable<any> {
    return this.store.select(CatalogSelectors.selectIsCategoriesLoading);
  }

  /**
   *
   * @param params
   * @returns {Observable<ICatalogSearchIncluded>}
   */
  getSearchResults(params: any): Observable<ICatalogSearchIncluded> {
    return this.productListService.getProductByCategorySpecificPage(params);
  }

  /**
   *
   * @param {string} functionalLocationNumber
   * @returns {Observable<IInstalledBaseSystems>}
   */
  getInstallBaseSystem(functionalLocationNumber: string): Observable<IInstalledBaseSystems> {
    return this.getInstallBaseService().getInstalledBaseSystems({functionalLocationNumber});
  }

  /**
   *
   * @param {string} flNumber
   * @returns {Observable<IInstalledBaseSystemsSoldTo>}
   */
  getInstalledBaseWithSoldTo(flNumber: string): Observable<IInstalledBaseSystemsSoldTo> {
    return this.getInstallBaseService().getInstalledBaseSystems(
      {functionalLocationNumber: flNumber},
      [EGlueResource.CUSTOMER_SOLD_TO_ID],
    ) as Observable<IInstalledBaseSystemsSoldTo>;
  }

  /**
   *
   * @param {string} sku
   */
  loadAbstractProductData(sku: string): void {
    this.store.dispatch(CatalogActions.loadAbstractProductData({sku}));
  }

  /**
   *
   * @param {string} sku
   * @returns {Observable<any>}
   */
  getAbstractProductDataFromApi(sku: string): Observable<any> {
    return this.getProductsService().getAbstractProductDataFromApi(sku);
  }

  /**
   *
   * @returns {Observable<any>}
   */
  selectAbstractProduct(): Observable<any> {
    return this.store.select(CatalogSelectors.selectAbstractProduct);
  }

  /**
   *
   * @returns {Observable<any>}
   */
  selectAbstractProductError(): Observable<any> {
    return this.store.select(CatalogSelectors.selectAbstractProductError);
  }

  /**
   *
   */
  clearAbstractProduct(): void {
    this.store.dispatch(CatalogActions.clearAbstractProductData());
  }

  /**
   *
   * @param {string} materialNumber
   * @param {string} functionalLocationNumber
   * @returns {Observable<IInstalledBaseSystems>}
   */
  getInstalledBaseSystemByFunctionalLocation(
    materialNumber: string,
    functionalLocationNumber: string,
  ): Observable<IInstalledBaseSystems> {
    return this.getInstallBaseService().getInstalledBaseSystems({
      materialNumber,
      functionalLocationNumber,
    });
  }

  /**
   *
   * @param {string} query
   */
  setSearchQuery(query: string): void {
    this.store.dispatch(CatalogActions.setSearchQuery({query}));
  }

  /**
   *
   * @returns {Observable<any>}
   */
  selectSearchQuery(): Observable<any> {
    return this.store.select(CatalogSelectors.selectSearchQuery);
  }

  /**
   *
   * @returns {Observable<any>}
   */
  selectInstalledBase(): Observable<any> {
    return this.store.select(CatalogSelectors.selectInstalledBase);
  }

  /**
   *
   * @param {ICurrentParams} params
   */
  setQueryParams(params: ICurrentParams): void {
    this.store.dispatch(CatalogActions.setQueryParams({params}));
  }

  /**
   *
   */
  clearSearchQuery(): void {
    this.store.dispatch(CatalogActions.clearSearchQuery());
  }

  /**
   *
   * @returns {Observable<any>}
   */
  selectQueryParams(): Observable<any> {
    return this.store.select(CatalogSelectors.selectQueryParams);
  }

  /**
   *
   * @param {ISimpleInstallBaseProduct} installedBase
   */
  setInstalledBase(installedBase: ISimpleInstallBaseProduct): void {
    this.store.dispatch(CatalogActions.setInstalledBase({installedBase}));
  }

  /**
   *
   * @param {string} value
   * @returns {Promise<any>}
   */
  getSuggestionsFromService(value: string): Promise<any> {
    return this.getSearchService().getSuggestions(value);
  }

  /**
   *
   * @returns {Observable<string[]>}
   */
  selectBlockedSortByOptions(): Observable<string[]> {
    return this.store.select(CatalogSelectors.selectBlockedSortByOptions);
  }

  /**
   *
   * @param {string} sku
   * @param {string} quoteUuid
   * @returns {Observable<any>}
   */
  getAbstractProductPricesAndAvailabilitiesFromSap(sku: string, quoteUuid: string): Observable<any> {
    return this.getProductsService().getAbstractProductPricesAndAvailabilitiesFromSap(sku, quoteUuid)
  }
}
